import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Camera, Bell, CheckCircle2, XCircle, ArrowRight, Shield } from 'lucide-react';

const KEY = 'carloo_perms_v2';

/**
 * Step-by-step permission gate. Shows BEFORE any other page (including login).
 *
 * On **native** (Android APK via Capacitor) — uses Capacitor's native plugins so
 * the user sees the real **Android system permission dialog** (not a browser prompt).
 *   - Location:      @capacitor/geolocation
 *   - Camera:        @capacitor/camera
 *   - Notifications: @capacitor/local-notifications (POST_NOTIFICATIONS on 13+)
 *
 * On **web** — falls back to the browser permissions APIs.
 *
 * If the OS permanently denies a permission (user tapped "Don't ask again"), the
 * native plugin surfaces `denied`; we show an inline "Open app settings" tap
 * that jumps them directly to the app settings screen (not to buried menus).
 */

const STEPS = [
  {
    id: 'gps',
    title: 'Location',
    icon: MapPin,
    accent: 'text-blue-600 bg-blue-100',
    blurb: 'Used to stamp your check-ins, visits, breaks and to verify you are inside the office geofence.',
    bullet: ['Precise GPS', 'Background tracking while on a visit', 'Never shared outside Carloo Tex'],
  },
  {
    id: 'camera',
    title: 'Camera',
    icon: Camera,
    accent: 'text-purple-600 bg-purple-100',
    blurb: 'Used to capture vehicle KM photos, supplier visit selfies and visitor proof-of-arrival.',
    bullet: ['Rear camera for KM photos', 'Front camera for selfies', 'Photos go only to your manager'],
  },
  {
    id: 'notif',
    title: 'Notifications',
    icon: Bell,
    accent: 'text-amber-600 bg-amber-100',
    blurb: 'So you (and your managers) get real-time alerts about unauthorised exits, approvals and visitor arrivals.',
    bullet: ['High-priority alerts with sound', 'Approval / rejection notices', 'Visitor arrived pings'],
  },
];

function isNative() {
  try {
    const cap = window.Capacitor;
    return !!(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
  } catch { return false; }
}

/**
 * Treat the device as "desktop" (=> skip the permission gate entirely) when:
 *   1. Not running inside the Capacitor APK shell, AND
 *   2. The user-agent does NOT match a mobile/tablet pattern, OR the browser
 *      reports it has no touch input, OR ``mediaDevices.enumerateDevices``
 *      returns zero ``videoinput`` entries (typical for desktop tower / NUC).
 *
 * Rationale (June 18, 2026 ask): admins and back-office staff log in from
 * Windows laptops without a camera; forcing them through the Camera /
 * Notifications prompts blocks them at the door. Geofence is still
 * enforced server-side at check-in, so the gate is purely UI friction
 * for desktop users.
 */
function isDesktop() {
  try {
    if (isNative()) return false;
    const ua = (navigator.userAgent || '').toLowerCase();
    const isMobileUA = /android|iphone|ipad|ipod|mobile|tablet|opera mini|iemobile|silk/i.test(ua);
    const hasTouch = (navigator.maxTouchPoints || 0) > 0 || 'ontouchstart' in window;
    // If UA clearly says mobile/tablet — keep the gate.
    if (isMobileUA) return false;
    // Desktop UA AND no touch screen → definitely desktop.
    if (!hasTouch) return true;
    // Otherwise (e.g. touch-laptop with mobile-ish UA) fall back to
    // the "no rear-facing camera" sniff in detectNoCamera() below.
    return true;
  } catch { return false; }
}

// ----- Native (Capacitor) permission handlers -----

async function nativeCheck(step) {
  try {
    if (step === 'gps') {
      const { Geolocation } = await import('@capacitor/geolocation');
      const s = await Geolocation.checkPermissions();
      const v = s.location || s.coarseLocation;
      if (v === 'granted') return 'granted';
      if (v === 'denied') return 'denied';
      return 'prompt';
    }
    if (step === 'camera') {
      const { Camera: Cam } = await import('@capacitor/camera');
      const s = await Cam.checkPermissions();
      const v = s.camera;
      if (v === 'granted' || v === 'limited') return 'granted';
      if (v === 'denied') return 'denied';
      return 'prompt';
    }
    if (step === 'notif') {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      const s = await LocalNotifications.checkPermissions();
      const v = s.display;
      if (v === 'granted') return 'granted';
      if (v === 'denied') return 'denied';
      return 'prompt';
    }
  } catch (e) {
    console.warn('[perm-check-native]', step, e);
  }
  return 'prompt';
}

async function nativeRequest(step) {
  try {
    if (step === 'gps') {
      const { Geolocation } = await import('@capacitor/geolocation');
      const s = await Geolocation.requestPermissions({ permissions: ['location', 'coarseLocation'] });
      const v = s.location || s.coarseLocation;
      return v === 'granted' ? 'granted' : (v === 'denied' ? 'denied' : 'prompt');
    }
    if (step === 'camera') {
      const { Camera: Cam } = await import('@capacitor/camera');
      const s = await Cam.requestPermissions({ permissions: ['camera'] });
      const v = s.camera;
      return (v === 'granted' || v === 'limited') ? 'granted' : (v === 'denied' ? 'denied' : 'prompt');
    }
    if (step === 'notif') {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      const s = await LocalNotifications.requestPermissions();
      const v = s.display;
      return v === 'granted' ? 'granted' : (v === 'denied' ? 'denied' : 'prompt');
    }
  } catch (e) {
    console.warn('[perm-request-native]', step, e);
  }
  return 'prompt';
}

async function openAppSettings() {
  try {
    const { App } = await import('@capacitor/app');
    if (App.openSettings) {
      await App.openSettings();
      return true;
    }
  } catch {}
  return false;
}

// ----- Web permission handlers (fallback) -----

async function webCheck(step) {
  if (step === 'gps' && navigator.permissions?.query) {
    try { return (await navigator.permissions.query({ name: 'geolocation' })).state; } catch {}
  }
  if (step === 'camera' && navigator.permissions?.query) {
    try { return (await navigator.permissions.query({ name: 'camera' })).state; } catch {}
  }
  if (step === 'notif' && 'Notification' in window) {
    const p = Notification.permission;
    if (p === 'granted') return 'granted';
    if (p === 'denied') return 'denied';
    return 'prompt';
  }
  return 'prompt';
}

async function webRequest(step) {
  if (step === 'gps') {
    if (!navigator.geolocation) return 'granted';
    return new Promise(resolve => {
      navigator.geolocation.getCurrentPosition(
        () => resolve('granted'),
        (err) => resolve(err?.code === 1 ? 'denied' : 'prompt'),
        { timeout: 15000, enableHighAccuracy: true }
      );
    });
  }
  if (step === 'camera') {
    if (!navigator.mediaDevices?.getUserMedia) return 'granted';
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      stream.getTracks().forEach(t => t.stop());
      return 'granted';
    } catch (e) {
      if (e?.name === 'NotAllowedError') return 'denied';
      return 'prompt';
    }
  }
  if (step === 'notif') {
    if (!('Notification' in window)) return 'granted';
    try {
      const p = await Notification.requestPermission();
      return p;
    } catch { return 'prompt'; }
  }
  return 'prompt';
}

const _check = async (step) => (isNative() ? nativeCheck(step) : webCheck(step));
const _request = async (step) => (isNative() ? nativeRequest(step) : webRequest(step));


export default function PermissionGate({ children }) {
  // Desktop bypass — admins / back-office staff log in from Windows
  // laptops without a camera. Forcing them through Camera + Notifications
  // prompts blocks login entirely. Geofence is still enforced server-side
  // at /api/attendance/checkin so this only removes UI friction.
  const [desktopBypass] = useState(() => isDesktop());

  const [statuses, setStatuses] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState('');

  const refresh = useCallback(async () => {
    const next = {};
    for (const s of STEPS) next[s.id] = await _check(s.id);
    setStatuses(next);
    const firstPending = STEPS.findIndex(s => next[s.id] !== 'granted');
    setCurrentIdx(firstPending === -1 ? STEPS.length : firstPending);
    return next;
  }, []);

  useEffect(() => {
    if (desktopBypass) return;          // skip entirely on desktop
    refresh();
  }, [refresh, desktopBypass]);

  const handleGrant = async () => {
    setBusy(true);
    setSettingsMsg('');
    const step = STEPS[currentIdx];
    const result = await _request(step.id);
    const next = { ...(statuses || {}), [step.id]: result };
    setStatuses(next);
    if (result === 'granted') setCurrentIdx(idx => idx + 1);
    setBusy(false);
  };

  const handleOpenSettings = async () => {
    const opened = await openAppSettings();
    if (!opened) setSettingsMsg('Could not open settings. Please open Settings → Apps → Carloo Tex → Permissions manually.');
    // Re-check after user returns
    setTimeout(refresh, 1500);
  };

  // Desktop bypass — render children immediately, no gate UI.
  if (desktopBypass) {
    try { localStorage.setItem(KEY, 'desktop-bypass'); } catch {}
    return children;
  }

  if (statuses === null) return null;

  const allGranted = STEPS.every(s => statuses[s.id] === 'granted');
  if (allGranted) {
    try { localStorage.setItem(KEY, '1'); } catch {}
    return children;
  }

  const step = STEPS[currentIdx] || STEPS[STEPS.length - 1];
  const state = statuses[step.id];
  const Icon = step.icon;
  const pos = currentIdx + 1;
  const native = isNative();

  return (
    <div className="fixed inset-0 z-[999] bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4" data-testid="permission-gate">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-center justify-center gap-2 mb-5" data-testid="perm-progress">
            {STEPS.map((s, i) => (
              <div key={s.id} className={`h-1.5 rounded-full transition-all ${
                statuses[s.id] === 'granted' ? 'bg-emerald-500 w-8'
                  : i === currentIdx ? 'bg-blue-600 w-8'
                  : 'bg-gray-200 w-4'
              }`} />
            ))}
          </div>

          <div className="text-center mb-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Step {pos} of {STEPS.length}
          </div>

          <div className="text-center mb-4">
            <div className={`inline-flex p-3 rounded-2xl mb-3 ${step.accent}`}>
              <Icon size={30} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Allow {step.title}</h1>
            <p className="text-sm text-gray-600 mt-1.5 px-2">{step.blurb}</p>
          </div>

          <ul className="space-y-1.5 mb-5 bg-gray-50 rounded-xl p-4" data-testid={`perm-details-${step.id}`}>
            {step.bullet.map((b, i) => (
              <li key={i} className="text-xs text-gray-700 flex items-start gap-2">
                <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          {state === 'denied' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs mb-3" data-testid={`perm-denied-${step.id}`}>
              <div className="flex items-start gap-2">
                <XCircle size={14} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-red-700">{step.title} is blocked</p>
                  {native ? (
                    <>
                      <p className="text-red-600 mt-1">
                        You previously tapped "Don't ask again". Open the app settings and enable {step.title} there.
                      </p>
                      <button
                        onClick={handleOpenSettings}
                        className="mt-2 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg"
                        data-testid={`perm-open-settings-${step.id}`}
                      >
                        Open app settings
                      </button>
                      {settingsMsg && <p className="mt-1 text-[11px] text-red-700">{settingsMsg}</p>}
                    </>
                  ) : (
                    <p className="text-red-600 mt-1">
                      Tap the 🔒 / <span className="font-mono">ⓘ</span> icon next to the URL bar, switch{' '}
                      <strong>{step.title}</strong> to <strong>Allow</strong>, then tap the button below.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <button
            onClick={state === 'denied' ? refresh : handleGrant}
            disabled={busy}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all"
            data-testid={`perm-grant-${step.id}`}
          >
            {busy ? 'Requesting…' : state === 'denied' ? 'I changed the setting — retry' : `Allow ${step.title}`}
            {!busy && <ArrowRight size={18} />}
          </button>

          <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-gray-500" data-testid="perm-security-note">
            <Shield size={11} />
            <span>Required — Carloo Tex cannot run without these permissions.</span>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">Carloo Tex • Field Force Management</p>
      </div>
    </div>
  );
}
